import axios from 'axios';
import config from '../config';
import { Buffer } from 'buffer';
import { v4 as uuidv4 } from 'uuid';

const mayaPaymentsUrl: string = config.maya_payments.url
const publicAuth: string = Buffer.from(`${config.maya_payments.pub_api_key}:`, 'binary').toString('base64')
const secretAuth: string = Buffer.from(`${config.maya_payments.sec_api_key}:`, 'binary').toString('base64')
const redirectUrl = `${config.host_url}/purchase`;

/* 
    For demo purposes, the following functions were done on the frontend. 
    However, when implementing for commercial use, please do the following transactions
    on your backend servers to protect the cardTokenId of the customer.
*/

export const createPaymentToken = async (newCardDetails: NewCardDetails) => {
    const headers = {
        accept: 'application/json',
        authorization: `Basic ${publicAuth}`,
    }
    const req: PaymentTokenRequest = createPaymentTokenRequest(newCardDetails)
    
    const response = await axios.post(`${mayaPaymentsUrl}/payment-tokens`, req, { headers })
    const cards: PaymentTokenResponse = response.data
    return cards
}

export const createPaymentTokenRequest = (newCardDetails: NewCardDetails) => {
    const { isDefault: _, ...card } = newCardDetails
    const req: PaymentTokenRequest = { card }
    
    return req
}

export const createCardPayment = async (customerId: string, cardTokenId: string, totalAmount: number) => {
    const headers = {
        accept: 'application/json',
        authorization: `Basic ${secretAuth}`,
    }
    const requestReferenceNumber = uuidv4();
    const cardPaymentRequest: CardPaymentRequest = createCardPaymentRequest(totalAmount, requestReferenceNumber)
    const response = await axios.post(`${mayaPaymentsUrl}/customers/${customerId}/cards/${cardTokenId}/payments`, cardPaymentRequest, { headers })
    const cardPayment: CardPayment = response.data
    return { requestReferenceNumber, cardPayment };
}

export const createCardPaymentRequest = (totalAmount: number, requestReferenceNumber: string) => {
    const req: CardPaymentRequest = {
        totalAmount: {
            amount: totalAmount,
            currency: 'PHP'
        },
        requestReferenceNumber,
        redirectUrl: {
            success: `${redirectUrl}/success/?id=${requestReferenceNumber}`,
            failure: `${redirectUrl}/failed/?id=${requestReferenceNumber}`,
            cancel: `${redirectUrl}/canceled/?id=${requestReferenceNumber}`
        }
    }

    return req
}

export const createOneTimePayment = async (cardTokenId: string, totalAmount: number) => {
    const headers = {
        accept: 'application/json',
        authorization: `Basic ${secretAuth}`,
    }
    const requestReferenceNumber = uuidv4();
    const cardPaymentRequest = createOneTimePaymentRequest(cardTokenId, totalAmount, requestReferenceNumber)
    const response = await axios.post(`${mayaPaymentsUrl}/payments`, cardPaymentRequest, { headers })
    const cardPayment: CardPayment = response.data
    return { requestReferenceNumber, cardPayment };
}

export const createOneTimePaymentRequest = (cardTokenId: string, totalAmount: number, requestReferenceNumber: string) => {
    const req = {
        paymentTokenId: cardTokenId,
        totalAmount: {
            amount: totalAmount,
            currency: 'PHP'
        },
        requestReferenceNumber,
        redirectUrl: {
            success: `${redirectUrl}/success/?id=${requestReferenceNumber}`,
            failure: `${redirectUrl}/failed/?id=${requestReferenceNumber}`,
            cancel: `${redirectUrl}/canceled/?id=${requestReferenceNumber}`
        }
    }

    return req
}

/*
    Google Pay integration (two-step flow):
    1. Create payment token with source: 'googlepay'
    2. Create payment with the resulting paymentTokenId + buyer details

    For demo purposes this is done on the frontend.
    In production, process this on your backend server.
*/
export const createGooglePayPaymentToken = async (paymentData: google.payments.api.PaymentData) => {
    const headers = {
        accept: 'application/json',
        authorization: `Basic ${publicAuth}`,
    }

    const rawToken = paymentData.paymentMethodData.tokenizationData.token;
    let token;
    try {
        token = typeof rawToken === 'string' ? JSON.parse(rawToken) : rawToken;
    } catch {
        throw new Error('Invalid Google Pay token format');
    }

    const req = {
        source: 'googlepay',
        paymentMethodData: { token },
    }

    const response = await axios.post(`${mayaPaymentsUrl}/payment-tokens`, req, { headers })
    return response.data as PaymentTokenResponse
}

export const createGooglePayPayment = async (paymentTokenId: string, totalAmount: number, paymentData: google.payments.api.PaymentData) => {
    const headers = {
        accept: 'application/json',
        authorization: `Basic ${secretAuth}`,
    }
    const requestReferenceNumber = uuidv4();

    const req = {
        paymentTokenId,
        totalAmount: {
            amount: Number(totalAmount.toFixed(2)),
            currency: 'PHP'
        },
        buyer: buildBuyerFromGooglePay(paymentData),
        requestReferenceNumber,
        redirectUrl: {
            success: `${redirectUrl}/success/?id=${requestReferenceNumber}`,
            failure: `${redirectUrl}/failed/?id=${requestReferenceNumber}`,
            cancel: `${redirectUrl}/canceled/?id=${requestReferenceNumber}`
        }
    }

    const response = await axios.post(`${mayaPaymentsUrl}/payments`, req, { headers })
    const cardPayment: CardPayment = response.data
    return { requestReferenceNumber, cardPayment }
}

export function splitName(fullName: string) {
    const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return { firstName: parts[0] || '', middleName: '', lastName: parts[0] || '' };
    return { firstName: parts[0], middleName: parts.slice(1, -1).join(' '), lastName: parts.at(-1) || '' };
}

export function buildBuyerFromGooglePay(paymentData: google.payments.api.PaymentData): Buyer {
    const billing = paymentData.paymentMethodData?.info?.billingAddress ?? {} as google.payments.api.Address;
    const shipping = paymentData.shippingAddress ?? {} as google.payments.api.Address;
    const billingName = splitName(billing.name || '');
    const shippingName = splitName(shipping.name || '');

    return {
        firstName: billingName.firstName,
        middleName: billingName.middleName,
        lastName: billingName.lastName,
        contact: { email: paymentData.email ?? '', phone: billing.phoneNumber ?? '' },
        billingAddress: {
            line1: billing.address1 ?? '', line2: billing.address2 ?? '', city: billing.locality ?? '',
            state: billing.administrativeArea ?? '', zipCode: billing.postalCode ?? '', countryCode: billing.countryCode ?? '',
        },
        shippingAddress: {
            line1: shipping.address1 ?? '', line2: shipping.address2 ?? '', city: shipping.locality ?? '',
            state: shipping.administrativeArea ?? '', zipCode: shipping.postalCode ?? '', countryCode: shipping.countryCode ?? '',
            firstName: shippingName.firstName, lastName: shippingName.lastName, phone: shipping.phoneNumber ?? '', email: paymentData.email ?? '',
        },
    };
}
